import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaStar,
  FaMapMarkerAlt,
  FaClock,
  FaUtensils,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { safeScrollToTop, shouldSkipAutoScroll, blockAutoScroll } from "../utils/scrollGuard";
import { APP_URLS } from "../config/urls.config";
import ModernPhotoGallery from "./ModernPhotoGallery";

const RestaurantDetailPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantPhotos, setRestaurantPhotos] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const pageRef = React.useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Scroll to absolute top on mount
    safeScrollToTop();
    try {
      if (!shouldSkipAutoScroll()) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchRestaurantDetails();
  }, [restaurantId]);

  const fetchRestaurantDetails = async () => {
    try {
      setLoading(true);

      // Fetch restaurant details
      const restaurantResponse = await axios.get(
        `${APP_URLS.API}/restaurants/${restaurantId}`
      );
      setRestaurant(restaurantResponse.data);

      // Fetch available tables for this restaurant
      const tablesResponse = await axios.get(
        `${APP_URLS.API}/restaurants/${restaurantId}/tables`
      );
      setTables(tablesResponse.data);

      // Fetch restaurant photos
      try {
        const photosResponse = await axios.get(
          `${APP_URLS.API}/restaurant-photos/${restaurantId}`
        );
        if (Array.isArray(photosResponse.data) && photosResponse.data.length > 0) {
          setRestaurantPhotos(photosResponse.data);
        } else {
          // Fallback to defaults
          setRestaurantPhotos([
            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&auto=format&fit=crop&q=80",
            "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=1200&auto=format&fit=crop&q=80",
            "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=1200&auto=format&fit=crop&q=80",
            "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=1200&auto=format&fit=crop&q=80",
            "https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200&auto=format&fit=crop&q=80",
          ]);
        }
      } catch (err) {
        setRestaurantPhotos([
          "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&auto=format&fit=crop&q=80",
          "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=1200&auto=format&fit=crop&q=80",
          "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=1200&auto=format&fit=crop&q=80",
        ]);
      }
    } catch (error) {
      console.error("Error fetching restaurant details:", error);
      toast.error("Failed to load restaurant details");
    } finally {
      setLoading(false);
    }
  };

  const handleTableClick = (table) => {
    if (table.is_available) {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('restaurantsScrollY', String(window.scrollY || 0));
          sessionStorage.setItem('focusRestaurantId', String(restaurantId));
        }
      } catch (e) {}
      try { blockAutoScroll(5000); } catch (e) {}
      navigate(`/restaurant-booking/${restaurantId}/${table.table_id}`, {
        state: { restaurant, table },
      });
    } else {
      toast.info("This table is already booked");
    }
  };

  const handleBackToRestaurants = () => {
    navigate("/restaurants");
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "90px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: isMobile ? "40px" : "60px",
              height: isMobile ? "40px" : "60px",
              border: "4px solid #FEE2E2",
              borderTop: "4px solid #EE2E24",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p
            style={{
              color: "#6B7280",
              fontSize: isMobile ? "0.85rem" : "1rem",
              fontWeight: "500",
            }}
          >
            Loading restaurant details...
          </p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "90px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "#6B7280",
              marginBottom: "20px",
            }}
          >
            Restaurant not found
          </h2>
          <button
            onClick={() => navigate("/restaurants")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #EE2E24 0%, #C62828 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Browse Restaurants
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pageRef}
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#2E2E2E",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingTop: isMobile ? "70px" : "90px",
      }}
    >
      {/* Back Button */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: isMobile ? "10px 15px" : "20px" }}>
        <button
          onClick={handleBackToRestaurants}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: isMobile ? "8px 16px" : "10px 20px",
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            fontWeight: "700",
            fontSize: isMobile ? "0.8rem" : "0.9rem",
            color: "#EE2E24",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
          }}
        >
          <FaArrowLeft /> {isMobile ? "Back" : "Back to Restaurants"}
        </button>
      </div>

      {/* Modern Gallery Section */}
      <section style={{ padding: "0 0 30px 0" }}>
        <ModernPhotoGallery 
          images={restaurantPhotos} 
          title="Restaurant Gallery" 
        />
      </section>

      {/* Restaurant Info Section */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto 30px auto",
          padding: isMobile ? "0 10px" : "0 20px",
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            padding: isMobile ? "18px" : "30px",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            border: "1px solid #F3F4F6",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "flex-start",
              gap: isMobile ? "12px" : "0",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: isMobile ? "22px" : "32px",
                  fontWeight: "800",
                  color: "#1a1a1a",
                  marginBottom: isMobile ? "10px" : "15px",
                }}
              >
                🍽️ {restaurant.name || restaurant.restaurantName}
              </h1>
              <div
                style={{
                  display: "flex",
                  gap: isMobile ? "12px" : "25px",
                  flexDirection: isMobile ? "column" : "row",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#4B5563",
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                  }}
                >
                  <FaMapMarkerAlt style={{ color: "#EE2E24" }} />
                  <span>{restaurant.location || restaurant.address}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#4B5563",
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                  }}
                >
                  <FaClock style={{ color: "#EE2E24" }} />
                  <span>
                    {restaurant.open_time} - {restaurant.close_time}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#4B5563",
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                  }}
                >
                  <FaUtensils style={{ color: "#EE2E24" }} />
                  <span>{restaurant.type}</span>
                </div>
              </div>
            </div>
            <div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  background: restaurant.isActive
                    ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                    : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                  color: "white",
                  borderRadius: "20px",
                  fontSize: isMobile ? "0.75rem" : "0.85rem",
                  fontWeight: "700",
                }}
              >
                <FaStar />
                {restaurant.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <section
        style={{
          padding: isMobile ? "30px 10px" : "50px 20px",
          background: "linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)",
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: isMobile ? "25px" : "35px",
            }}
          >
            <h2
              style={{
                fontSize: isMobile ? "1.8rem" : "clamp(2rem, 3.5vw, 2.5rem)",
                fontWeight: "700",
                background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "8px",
              }}
            >
              🪑 Available Tables
            </h2>
            <p
              style={{
                fontSize: isMobile ? "0.85rem" : "1rem",
                color: "#6B7280",
                fontWeight: "500",
              }}
            >
              {tables.filter((table) => table.is_available).length} of{" "}
              {tables.length} tables available
            </p>
          </div>

          {tables.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: isMobile ? "40px 15px" : "60px 20px",
                color: "#9CA3AF",
              }}
            >
              <div style={{ fontSize: isMobile ? "2.5rem" : "3rem", marginBottom: "15px" }}>🍽️</div>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                No tables found
              </div>
              <div style={{ fontSize: "0.9rem" }}>Please check back later</div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
                gap: isMobile ? "20px" : "25px",
              }}
            >
              {tables.map((table) => (
                <div
                  key={table.table_id}
                  style={{
                    position: "relative",
                    background: "#FFFFFF",
                    borderRadius: "15px",
                    boxShadow: table.is_available
                      ? "0 8px 20px rgba(238, 46, 36, 0.12)"
                      : "0 4px 12px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: table.is_available ? "pointer" : "not-allowed",
                    opacity: table.is_available ? 1 : 0.6,
                    border: table.is_available
                      ? "2px solid transparent"
                      : "2px solid #E5E7EB",
                  }}
                  onClick={() => handleTableClick(table)}
                >
                  {/* Availability Badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: isMobile ? "12px" : "15px",
                      right: isMobile ? "12px" : "15px",
                      padding: isMobile ? "4px 10px" : "6px 12px",
                      borderRadius: "20px",
                      fontSize: isMobile ? "0.65rem" : "0.75rem",
                      fontWeight: "700",
                      background: table.is_available
                        ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                        : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      color: "#FFFFFF",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    {table.is_available ? "✓ Available" : "✕ Booked"}
                  </div>

                  {/* Table Icon */}
                  <div
                    style={{
                      padding: isMobile ? "25px 15px 15px 15px" : "30px 20px 20px 20px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "3.5rem" : "4rem",
                        marginBottom: isMobile ? "10px" : "15px",
                        filter: table.is_available
                          ? "grayscale(0%)"
                          : "grayscale(100%)",
                      }}
                    >
                      🪑
                    </div>

                    {/* Table Number */}
                    <h3
                      style={{
                        fontSize: isMobile ? "1.2rem" : "1.4rem",
                        fontWeight: "800",
                        marginBottom: "6px",
                        color: "#1F2937",
                      }}
                    >
                      Table {table.table_number}
                    </h3>

                    {/* Seating Capacity */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        marginBottom: isMobile ? "15px" : "20px",
                        color: "#6B7280",
                        fontSize: isMobile ? "0.85rem" : "0.95rem",
                        fontWeight: "600",
                      }}
                    >
                      <span style={{ fontSize: isMobile ? "1.1rem" : "1.2rem" }}>👥</span>
                      <span>{table.seating_capacity} seats</span>
                    </div>

                    {/* Book Now Button */}
                    {table.is_available && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTableClick(table);
                        }}
                        style={{
                          width: "100%",
                          padding: isMobile ? "11px 18px" : "12px 20px",
                          background:
                            "linear-gradient(135deg, #EE2E24 0%, #C62828 100%)",
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: "10px",
                          fontSize: isMobile ? "0.9rem" : "0.95rem",
                          fontWeight: "700",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(238, 46, 36, 0.3)",
                        }}
                      >
                        Reserve Table
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default RestaurantDetailPage;
